import type Immutable from 'immutable';
import { VideoData } from '@lumen5/luminary';

import type { RootState } from 'js/src/libs/RootState';

function getScenes(state: RootState) {
    return state.scenes;
}

/**
 * If the insert selector is being shown
 */
export function isOverlaySelectorVisible(state: RootState): boolean {
    return getScenes(state).overlaySelectorShowing;
}

/**
 * If the fullscreen scene selector is being shown
 */
export function isFullscreenSelectorVisible(state: RootState): boolean {
    return getScenes(state).showFullscreenSceneSelector.get('visible');
}

/**
 * If the fullscreen scene selector is being shown
 */
export function isFullscreenSwapSceneSelectorVisible(state: RootState): boolean {
    return getScenes(state).showFullscreenSwapSceneSceneSelector;
}

/**
 * Get scene id for swap scene design tab
 */
export function getSwapSceneDesignSlideId(state: RootState): string | null {
    return getScenes(state).swapSceneDesignSlideId;
}

/**
 * Get slide id for new scene tab
 */
export function getSceneSelectorSlideId(state: RootState): string | null {
    return getScenes(state).sceneSelectorSlideId;
}

/**
 * Get the start and end times of an overlay
 *
 * @returns an object containing the startTime and endTime of an overlay that could be created
 *
 * Note that both startTime and endTime are in video time.
 */
export function getOverlayBounds(state: RootState) {
    return getScenes(state).overlayBounds;
}

export function getOverlaySceneGroupId(state: RootState): string | null {
    return getScenes(state).overlaySceneGroupId;
}

/**
 * Determines whether either the scene selector or swap scene design tab is open
 */
export function isSceneSectionVisible(state: RootState): boolean {
    return getSceneSelectorSlideId(state) !== null || getSwapSceneDesignSlideId(state) !== null;
}

/**
 * Determines if the new scene tab or the swap scene design tab is open for the specified slide
 */
export function isSceneSectionVisibleForSlide(state: RootState, slideId: string): boolean {
    return (
        getSceneSelectorSlideId(state) === slideId || getSwapSceneDesignSlideId(state) === slideId
    );
}

/**
 * Get tracked scene edited text asset id list.
 */
export function getTrackedSceneEditedTextAssetsIds(
    state: RootState,
    sceneId: string,
): Immutable.List<string> | null {
    const scenes = getScenes(state);
    let trackedSceneTextAssetIds = null;
    // must find sceneId otherwise return false
    if (scenes && scenes.sceneDesignTextAssetChangedSet.has(sceneId)) {
        trackedSceneTextAssetIds = scenes.sceneDesignTextAssetChangedSet.get(sceneId);
        if (trackedSceneTextAssetIds === undefined) {
            trackedSceneTextAssetIds = null;
        }
    }
    return trackedSceneTextAssetIds;
}

export function isAssetFocusedOnSubscene(
    state: RootState,
    sceneId: string,
    subsceneIndex: number,
): boolean {
    if (!state.scenes) {
        // not in the creator, can't be editing
        return false;
    }
    // check if any assets are being edited at all
    if (getFocusedSceneInfo(state).get('assetId') === null) {
        return false;
    }
    // something's being edited, but is it on this scene/subscene?
    return (
        getFocusedSceneInfo(state).get('sceneId') === sceneId &&
        getFocusedSceneInfo(state).get('subsceneIndex') === subsceneIndex
    );
}

export function isSubsceneFocused(
    state: RootState,
    sceneId: string,
    subsceneIndex: number,
): boolean {
    if (!state.scenes) {
        // not in the creator, can't be editing
        return false;
    }
    return (
        getFocusedSceneInfo(state).get('sceneId') === sceneId &&
        getFocusedSceneInfo(state).get('subsceneIndex') === subsceneIndex
    );
}

export function isAssetFocused(
    state: RootState,
    sceneId: string,
    subsceneIndex: number,
    assetId: string,
): boolean {
    if (!state.scenes) {
        // not in the creator, can't be editing
        return false;
    }
    return (
        getFocusedSceneInfo(state).get('sceneId') === sceneId &&
        getFocusedSceneInfo(state).get('subsceneIndex') === subsceneIndex &&
        getFocusedSceneInfo(state).get('assetId') === assetId
    );
}

export function getFocusedMediaAssetForScene(
    state: RootState,
    sceneId: string,
    subsceneIndex: number,
): string | null {
    if (!state.scenes) {
        // not in the creator, can't be editing
        return null;
    }
    // check if any assets are being edited at all
    if (getFocusedSceneInfo(state).get('assetId') === null) {
        return null;
    }
    // something's being edited, but is it on this scene/subscene?
    if (
        getFocusedSceneInfo(state).get('sceneId') !== sceneId ||
        getFocusedSceneInfo(state).get('subsceneIndex') !== subsceneIndex
    ) {
        return null;
    }
    // something's being edited on this scene, but is it a media asset?
    if (getFocusedSceneInfo(state).get('assetType') !== VideoData.MEDIA_ASSET_TYPE) {
        return null;
    }
    // we are editing on this scene/subscene!
    return getFocusedSceneInfo(state).get('assetId');
}

export function getFocusedIconAssetForScene(
    state: RootState,
    sceneId: string,
    subsceneIndex: number,
): string | null {
    if (!state.scenes) {
        // not in the creator, can't be editing
        return null;
    }
    // check if any assets are being edited at all
    if (getFocusedSceneInfo(state).get('assetId') === null) {
        return null;
    }
    // something's being edited, but is it on this scene/subscene?
    if (
        getFocusedSceneInfo(state).get('sceneId') !== sceneId ||
        getFocusedSceneInfo(state).get('subsceneIndex') !== subsceneIndex
    ) {
        return null;
    }
    // something's being edited on this scene, but is it an icon asset?
    if (getFocusedSceneInfo(state).get('assetType') !== VideoData.ICON_ASSET_TYPE) {
        return null;
    }
    // we are editing on this scene/subscene!
    return getFocusedSceneInfo(state).get('assetId');
}

export function getFocusedFancyCaptionAssetForScene(
    state: RootState,
    sceneId: string,
    subsceneIndex: number,
): string | null {
    if (!state.scenes) {
        // not in the creator, can't be editing
        return null;
    }
    // check if any assets are being edited at all
    if (getFocusedSceneInfo(state).get('assetId') === null) {
        return null;
    }
    // something's being edited, but is it on this scene/subscene?
    if (
        getFocusedSceneInfo(state).get('sceneId') !== sceneId ||
        getFocusedSceneInfo(state).get('subsceneIndex') !== subsceneIndex
    ) {
        return null;
    }
    // something's being edited on this scene, but is it a fancy caption asset?
    if (getFocusedSceneInfo(state).get('assetType') !== VideoData.FANCY_CAPTION_ASSET_TYPE) {
        return null;
    }
    // we are editing on this scene/subscene!
    return getFocusedSceneInfo(state).get('assetId');
}

export function getFocusedAssetId(state: RootState): string | null {
    if (!state.scenes) {
        // not in the creator, can't be editing
        return null;
    }
    // check if any assets are being edited at all
    if (getFocusedSceneInfo(state).get('assetId') === null) {
        return null;
    }
    return getFocusedSceneInfo(state).get('assetId');
}

export function getFocusedAssetIdForScene(
    state: RootState,
    sceneId: string,
    subsceneIndex: number,
): string | null {
    if (!state.scenes) {
        // not in the creator, can't be editing
        return null;
    }
    // something's being edited, but is it on this scene/subscene?
    if (
        getFocusedSceneInfo(state).get('sceneId') !== sceneId ||
        getFocusedSceneInfo(state).get('subsceneIndex') !== subsceneIndex
    ) {
        return null;
    }
    // check if any assets are being edited at all
    if (getFocusedSceneInfo(state).get('assetId') === null) {
        return null;
    }
    return getFocusedSceneInfo(state).get('assetId');
}

export function getFocusedSceneGroupId(state: RootState): string | null {
    if (!state.scenes) {
        // not in the creator, can't be editing
        return null;
    }
    return getFocusedSceneInfo(state).get('sceneGroupId');
}

export function getFocusedSceneId(state: RootState): string | null {
    if (!state.scenes) {
        // not in the creator, can't be editing
        return null;
    }
    return getFocusedSceneInfo(state).get('sceneId');
}

export function getFocusedSubsceneIndex(state: RootState): number | null {
    if (!state.scenes) {
        // not in the creator, can't be editing
        return null;
    }
    // check if any assets are being edited at all
    if (getFocusedSceneInfo(state).get('assetId') === null) {
        return null;
    }
    return getFocusedSceneInfo(state).get('subsceneIndex');
}

export function getFocusedAssetType(state: RootState): string | null {
    if (!state.scenes) {
        // not in the creator, can't be editing
        return null;
    }
    // check if any assets are being edited at all
    if (getFocusedSceneInfo(state).get('assetId') === null) {
        return null;
    }
    return getFocusedSceneInfo(state).get('assetType');
}

export function getFocusedAssetTypeForScene(
    state: RootState,
    sceneId: string,
    subsceneIndex: number,
): string | null {
    if (!state.scenes) {
        // not in the creator, can't be editing
        return null;
    }

    // something's being edited, but is it on this scene/subscene?
    if (
        getFocusedSceneInfo(state).get('sceneId') !== sceneId ||
        getFocusedSceneInfo(state).get('subsceneIndex') !== subsceneIndex
    ) {
        return null;
    }

    // check if any assets are being edited at all
    if (getFocusedSceneInfo(state).get('assetId') === null) {
        return null;
    }
    return getFocusedSceneInfo(state).get('assetType');
}

export function getFocusedRootAssetGroupId(state: RootState): string | null {
    if (!state.scenes) {
        // not in the creator, can't be editing
        return null;
    }
    // check if any assets are being edited at all
    if (getFocusedSceneInfo(state).get('assetId') === null) {
        return null;
    }
    return getFocusedSceneInfo(state).get('rootAssetGroupId') ?? null;
}

export function getFocusedSceneInfo(state: RootState) {
    return state.scenes.focusedSceneInfo;
}

export function getIsMorphSwapMode(state: RootState): boolean {
    return getScenes(state).isMorphSwapMode;
}

export function getIsPasteUploadingForAsset(state: RootState, assetId: string): boolean {
    return state.scenes.pasteUploads.has(assetId);
}

export function getPasteUploadSessionIdForAsset(state: RootState, assetId: string): string | null {
    const pasteUpload = state.scenes.pasteUploads.get(assetId);
    return pasteUpload?.get('uploadSessionId') ?? null;
}

export function getPasteUploadThumbnailForAsset(state: RootState, assetId: string): string | null {
    const pasteUpload = state.scenes.pasteUploads.get(assetId);
    return pasteUpload?.get('thumbnailDataUrl') ?? null;
}
